import { PERMISSIONS } from 'api/User/types'
import classNames from 'classnames'
import { InfoElement } from 'components'
import { InfoArrow } from 'components/InfoArrow'
import { CurrencyValue } from 'CurrencyValue'
import type { DetailsData } from 'data/types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import RestrictedView from 'RestrictedView'
import { label } from 'styles'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

import useStyles from './useStyles'
import { getCurrencyValue, getPercent } from './utils'

interface Props {
  data: DetailsData
}

function DetailsBudget({ data }: Props) {
  const { t: translate } = useTranslation()
  const classes = useStyles({ isPlanCompleted: data.revenuePlanDiff ? data.revenuePlanDiff > 0 : false })

  return (
    <Grid container item xs={9}>
      <Grid container item xs={12} className={classes.row1} mt="0.7rem">
        <Grid container item xs={5} alignItems="baseline" display="row">
          <CurrencyValue baseFontSize={1.75} currency={data.currency} value={data.revenueCurrent} />
          <div className={classes.revenuePlan}>
            {getCurrencyValue(data.revenuePlanDiff, data.currency, translate)}
            <InfoElement className={classes.percent} percent={data.revenuePlan} />
          </div>
        </Grid>
        <Grid container item xs={7} display="row">
          <Box className={`fm fm-l4l ${classes.label}`} alignSelf="baseline" />
          {getCurrencyValue(data.revenueCurrentL4L, data.currency, translate)}
          <InfoElement className={classes.percent} color={label} percent={data.revenuePlanL4L} />
          <div className={classes.separator} />
          {getCurrencyValue(data.revenuePerStoreCurrentL4L, data.currency, translate)}
        </Grid>
      </Grid>

      <Grid container item xs={5} alignItems="baseline" display="row" mt="0.25rem">
        <RestrictedView permission={PERMISSIONS.MARGIN}>
          <i className={`fm fm-margin ${classes.label}`} />
          <div className={classes.marginPercent}>{getPercent(data.marginCurrent, translate)}</div>
          <div>
            <div className={classes.marginValue}>{getCurrencyValue(data.marginValueCurrent, data.currency, translate)}</div>
            <InfoArrow current={data.marginValueCurrent} compared={data.marginValuePlan} />
          </div>
        </RestrictedView>
      </Grid>
      <Grid container item xs={7} alignItems="baseline" display="row" mt="0.25rem">
        <i className={classNames('fm fm-at', classes.label)} />
        <div className={classes.ecomShare}>
          <div className={classes.ecom}>{getPercent(data.ecomShareCurrent, translate)}</div>
        </div>
        <Box alignItems="baseline" className={classes.marginComparedValue} display="flex">
          {getCurrencyValue(data.revenueCurrentECom, data.currency, translate)}
          <InfoElement className={classes.percent} color={label} percent={data.revenuePlanECom} />
        </Box>
      </Grid>

      <Grid container item xs={5} display="row" color={label}>
        <RestrictedView permission={PERMISSIONS.MARGIN}>
          <i className={classes.label} />
          <div className={classes.marginPercent}>{getPercent(data.marginPlan, translate)}</div>
          <div className={classes.marginComparedValue}>{getCurrencyValue(data.marginValuePlan, data.currency, translate)}</div>
        </RestrictedView>
      </Grid>
      <Grid container item xs={7} alignItems="baseline" display="row">
        <div className={classNames(classes.label, classes.avg)}>m²</div>
        {getCurrencyValue(data.floorSpaceCurrent, 'm²', translate, 0)}
        <InfoElement className={classes.percent} color={label} percent={data.floorSpacePlan} />
      </Grid>

      <Grid container item xs={11} className={classes.row3}>
        &nbsp;
      </Grid>
      <Grid item xs={1} className={classes.row3} />
    </Grid>
  )
}

export default DetailsBudget
